import React, { useState, useEffect } from "react";
import { supabase } from "../integrations/supabase/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "../components/ui/card";
import { toast } from "sonner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { User } from "@supabase/supabase-js";
import { useDebugMode } from "../contexts/DebugModeContext";
import { useTheme } from "../contexts/ThemeContext";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function SettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [workoutReminders, setWorkoutReminders] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const { debugMode, toggleDebugMode } = useDebugMode();
  const { theme, setTheme } = useTheme();

  // Load user data and preferences
  useEffect(() => {
    async function loadUserData() {
      setLoading(true);
      try {
        // Get authenticated user
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (user) {
          setUser(user);
          setEmail(user.email || "");

          // Get user profile from database
          const { data: profile, error } = await supabase
            .from("user_profiles")
            .select("*")
            .eq("user_id", user.id)
            .single();

          if (error && error.code !== "PGRST116") {
            console.error("Error fetching profile:", error);
            toast.error("Failed to load profile data");
          }

          if (profile) {
            // Set profile data
            setDisplayName(profile.display_name || "");
            setBio(profile.bio || "");

            // Set preferences
            setNotificationsEnabled(profile.notifications_enabled ?? false);
            setWorkoutReminders(profile.workout_reminders ?? false);
            setSoundEnabled(profile.sound_enabled ?? true);
            setVoiceEnabled(profile.voice_enabled ?? true);
          }
        }
      } catch (error) {
        console.error("Error loading user data:", error);
        toast.error("Failed to load user data");
      } finally {
        setLoading(false);
      }
    }

    loadUserData();
  }, []);

  // Handle profile update
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);

    try {
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Update profile in database
      const { error } = await supabase.from("user_profiles").upsert({
        user_id: user.id,
        display_name: displayName,
        bio: bio,
        updated_at: new Date().toISOString(),
      });

      if (error) {
        throw error;
      }

      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  // Handle preferences update
  const savePreferences = async () => {
    setLoading(true);

    try {
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Update preferences in database
      const { error } = await supabase.from("user_profiles").upsert({
        user_id: user.id,
        notifications_enabled: notificationsEnabled,
        workout_reminders: workoutReminders,
        sound_enabled: soundEnabled,
        voice_enabled: voiceEnabled,
        updated_at: new Date().toISOString(),
      });

      if (error) {
        throw error;
      }

      toast.success("Preferences saved successfully!");
    } catch (error) {
      console.error("Error saving preferences:", error);
      toast.error("Failed to save preferences");
    } finally {
      setLoading(false);
    }
  };

  // Handle theme change
  const handleThemeChange = (isDark: boolean) => {
    setTheme(isDark ? "dark" : "light");

    // Save theme preference
    if (user) {
      supabase
        .from("user_profiles")
        .upsert({
          user_id: user.id,
          theme: isDark ? "dark" : "light",
          updated_at: new Date().toISOString(),
        })
        .then(({ error }) => {
          if (error) {
            console.error("Error saving theme preference:", error);
          }
        });
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    setIsDeleting(true);

    try {
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Delete user data from database
      const { error: profileError } = await supabase
        .from("user_profiles")
        .delete()
        .eq("user_id", user.id);

      if (profileError) {
        console.error("Error deleting profile:", profileError);
      }

      // Delete user workouts
      const { error: workoutsError } = await supabase
        .from("user_workouts")
        .delete()
        .eq("user_id", user.id);

      if (workoutsError) {
        console.error("Error deleting workouts:", workoutsError);
      }

      // Delete user account
      const { error } = await supabase.auth.admin.deleteUser(user.id);

      if (error) {
        throw error;
      }

      // Sign out
      await supabase.auth.signOut();

      toast.success("Account deleted successfully");
      navigate("/login");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Failed to delete account");
      setIsDeleting(false);
    }
  };

  if (loading && !user) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your account settings and preferences.
        </p>
      </div>

      <Tabs defaultValue="account" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="advanced">Advanced</TabsTrigger>
        </TabsList>

        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                Update your profile information and manage your account.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled
                  />
                  <p className="text-sm text-muted-foreground">
                    Your email address is managed through your authentication
                    provider.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your display name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell us a bit about yourself"
                    rows={3}
                  />
                </div>

                <Button type="submit" disabled={savingProfile}>
                  {savingProfile ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col items-start border-t pt-6">
              <h3 className="text-lg font-medium text-destructive mb-2">
                Danger Zone
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Permanently delete your account and all of your data.
              </p>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">Delete Account</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Are you absolutely sure?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete
                      your account and remove all of your data from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        "Delete Account"
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="preferences">
          <Card>
            <CardHeader>
              <CardTitle>Appearance & Behavior</CardTitle>
              <CardDescription>
                Customize how the application looks and behaves.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="dark-mode">Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Switch between light and dark themes.
                  </p>
                </div>
                <Switch
                  id="dark-mode"
                  checked={theme === "dark"}
                  onCheckedChange={handleThemeChange}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="sound-enabled">Sound Effects</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable sound effects during workouts.
                  </p>
                </div>
                <Switch
                  id="sound-enabled"
                  checked={soundEnabled}
                  onCheckedChange={(checked) => {
                    setSoundEnabled(checked);
                    savePreferences();
                  }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="voice-enabled">Voice Commands</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable voice command recognition during workouts.
                  </p>
                </div>
                <Switch
                  id="voice-enabled"
                  checked={voiceEnabled}
                  onCheckedChange={(checked) => {
                    setVoiceEnabled(checked);
                    savePreferences();
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Settings</CardTitle>
              <CardDescription>
                Configure how and when you receive notifications.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="notifications">Enable Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow the app to send you notifications.
                  </p>
                </div>
                <Switch
                  id="notifications"
                  checked={notificationsEnabled}
                  onCheckedChange={(checked) => {
                    setNotificationsEnabled(checked);
                    savePreferences();
                  }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="workout-reminders">Workout Reminders</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive reminders for scheduled workouts.
                  </p>
                </div>
                <Switch
                  id="workout-reminders"
                  checked={workoutReminders}
                  disabled={!notificationsEnabled}
                  onCheckedChange={(checked) => {
                    setWorkoutReminders(checked);
                    savePreferences();
                  }}
                />
              </div>

              {!notificationsEnabled && workoutReminders && (
                <div className="flex items-center p-3 text-sm bg-muted rounded-md">
                  <AlertCircle className="h-4 w-4 mr-2 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    Enable notifications to receive workout reminders.
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="advanced">
          <Card>
            <CardHeader>
              <CardTitle>Developer Options</CardTitle>
              <CardDescription>
                Advanced settings for debugging and development.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="debug-mode">Debug Mode</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable debugging tools for workout analysis and
                    troubleshooting.
                  </p>
                </div>
                <Switch
                  id="debug-mode"
                  checked={debugMode}
                  onCheckedChange={toggleDebugMode}
                />
              </div>

              {debugMode && (
                <div className="flex items-center p-3 text-sm bg-green-50 dark:bg-green-900/20 rounded-md">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                  <span className="text-green-700 dark:text-green-300">
                    Debug mode is active. Debug panels will be available
                    throughout the app.
                  </span>
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="app-version">App Version</Label>
                <div className="text-sm text-muted-foreground">
                  <p>Version: 1.0.0</p>
                  <p></p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
